import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import type { Props } from '@swf/components/StarsButton/StarsButton';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Tooltip from '@swf/components/Tooltip/Tooltip';
import Translation from '@swf/components/Translation/Translation';
import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';

import { TXTWCL_Copied } from './ShareClub.messages.json';
import styles from './ShareClub.scss';
import type { CopyData } from './hooks/useCopyInstructions';
import useCopyInstructions from './hooks/useCopyInstructions';

export interface ShareProps {
  clubId: CopyData['clubId'];
  buttonProps: Props;
  invitationCode?: CopyData['invitationCode'];
}

const ShareClub: React.FC<ShareProps> = ({
  clubId,
  invitationCode,
  buttonProps: { id, className, disabled, ...rest }
}) => {
  const { copied, handleCopy, resetCopy } = useCopyInstructions();
  const buttonRef = useRef(null);

  // reset copied state if a click occurs anywhere outside the copy instructions button
  useOnClickOutside(buttonRef, () => resetCopy());

  useEffect(() => {
    // reset copied state if user switches to another window
    window.addEventListener('blur', resetCopy);
    return () => window.removeEventListener('blur', resetCopy);
  }, [resetCopy]);

  const handleClick = () => {
    if (invitationCode) {
      handleCopy({ clubId, invitationCode });
    }
    return;
  };

  return (
    <ButtonGroup className={styles.buttonGroup}>
      <div ref={buttonRef}>
        <StarsButton
          id={id || 'shareClubButton'}
          className={classNames([styles.button, className])}
          disabled={disabled || copied}
          onClick={handleClick}
          {...rest}
        />
      </div>
      <Tooltip isOpen={copied} createPortal={false} placement="top">
        {({ ref, placement }) => (
          <div className={styles.tooltip} ref={ref} data-popper-placement={placement}>
            <Translation {...TXTWCL_Copied} />
          </div>
        )}
      </Tooltip>
    </ButtonGroup>
  );
};

export default ShareClub;
